import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {tap} from 'rxjs/operators';
import {Auth} from '../actions/auth.actions';
import ClearState = Auth.ClearState;
import {GetAnalyticsRequest} from '../models/analytics/analytics.request';
import {GetAnalyticsResponse} from '../models/analytics/analytics.response';
import {AnalyticsService} from '../services/analytics.service';
import {Analytics} from '../actions/analytics.actions';
import GetAnalytics = Analytics.GetAnalytics;
import {AnalyticsType} from '../models/analytics/analytics.model';
import {Account} from '../models/accounts/account.model';
import {Labour} from '../models/labour/labour.model';
import {RefrigeratorUnit} from '../models/refrigerator/refrigeratorUnit.model';
import {Engine} from '../models/engine/engine.model';
import UpdateAnalyticsSettings = Analytics.UpdateAnalyticsSettings;

export interface AnalyticsStateModel {
  request: GetAnalyticsRequest;
  response: GetAnalyticsResponse | undefined;
  settings: AnalyticsSettings;
}

export interface AnalyticsSettings {
  type: AnalyticsType;
  cumulative: boolean;
  potential: boolean;
  predict: boolean;
  debtorsSelected: Account[];
  labourSelected: Labour[];
  refrigeratorsSelected: RefrigeratorUnit[];
  enginesSelected: Engine[];
}

@State<AnalyticsStateModel>({
  name: 'analytics',
  defaults: {
    request: {
      period: 'YTD',
      customMonth: 0,
      customNumberOfMonths: 0,
      comparisons: {
        years: [],
        previousMonth: false
      }
    },
    settings: {
      type: 'TOTAL',
      cumulative: true,
      potential: false,
      predict: false,
      debtorsSelected: [],
      labourSelected: [],
      refrigeratorsSelected: [],
      enginesSelected: [],
    },
    response: null
  }
})
@Injectable()
export class AnalyticsState {
  constructor(private analyticsService: AnalyticsService) {
  }

  @Selector()
  static analytics(state: AnalyticsStateModel): GetAnalyticsResponse {
    return state.response;
  }

  @Selector()
  static request(state: AnalyticsStateModel): GetAnalyticsRequest {
    return state.request;
  }

  @Selector()
  static settings(state: AnalyticsStateModel): AnalyticsSettings {
    return state.settings;
  }

  @Action(GetAnalytics)
  getAnalytics(ctx: StateContext<AnalyticsStateModel>, action: GetAnalytics): Observable<GetAnalyticsResponse> {
    return this.analyticsService.getAnalytics(action.request).pipe(
      tap(res => {
        ctx.patchState({
          request: action.request,
          response: res,
        });
      }),
    );
  }

  @Action(UpdateAnalyticsSettings)
  updateAnalyticsSettings(ctx: StateContext<AnalyticsStateModel>, action: UpdateAnalyticsSettings): void {
    ctx.patchState({
      settings: action.settings
    });
  }

  @Action(ClearState)
  clearState(ctx: StateContext<AnalyticsStateModel>): void {
    ctx.setState({
      request: {
        period: 'YTD',
        customNumberOfMonths: 0,
        customMonth: 0,
        comparisons: {
          years: [],
          previousMonth: false
        }
      },
      settings: {
        type: 'TOTAL',
        cumulative: true,
        potential: false,
        predict: false,
        debtorsSelected: [],
        labourSelected: [],
        refrigeratorsSelected: [],
        enginesSelected: [],
      },
      response: null
    });
  }
}
